/**
 * Example slots: one help article, many business types.
 *
 * Article HTML carries `<p>{{example:<slot>}}</p>` tokens; the article's
 * `help_center_article_extra_json.examples[slot]` holds one HTML fragment per
 * edition (`default` plus business-type slugs such as `pilates`), per language.
 * `example_categories` maps the app's `settings_business_category` values onto
 * those edition slugs. Renderers (website, admin app, AI help) swap each token
 * for the right fragment; nothing else needs to know the slots exist.
 */
import type {
  IHelpExampleCategories,
  IHelpExampleSlots,
  THelpExampleLang,
} from '@/interfaces/helpExamples';

export const DEFAULT_EXAMPLE_EDITION = 'default';

const SLOT_TOKEN = /<p>\s*\{\{example:([\w-]+)\}\}\s*<\/p>|\{\{example:([\w-]+)\}\}/g;

export function hasExampleSlots(html: string | null | undefined): boolean {
  return Boolean(html && /\{\{example:[\w-]+\}\}/.test(html));
}

/** Edition slug for a business category (`pilates`, `yogaClasses`…), or `default`. */
export function editionForCategory(
  categories: IHelpExampleCategories | undefined,
  category: string | null | undefined
): string {
  return (category && categories?.[category]) || DEFAULT_EXAMPLE_EDITION;
}

/** The fragment for one slot: the edition's, else the default's, else nothing. */
export function exampleFragment(
  examples: IHelpExampleSlots | undefined,
  slot: string,
  edition: string,
  lang: THelpExampleLang
): string {
  const bucket = examples?.[slot];
  return bucket?.[edition]?.[lang] ?? bucket?.[DEFAULT_EXAMPLE_EDITION]?.[lang] ?? '';
}

/**
 * Replace every slot token in `html`. With `marked`, each fragment is wrapped in
 * HTML comments (`<!--example:slot-->…<!--/example:slot-->`) so a client-side
 * picker can swap editions without re-rendering the page; comments never affect
 * styling, unlike wrapper elements.
 */
export function renderExampleSlots(
  html: string,
  examples: IHelpExampleSlots | undefined,
  edition: string,
  lang: THelpExampleLang,
  options: { marked?: boolean } = {}
): string {
  if (!hasExampleSlots(html)) return html;
  return html.replace(SLOT_TOKEN, (_match, blockSlot: string | undefined, inlineSlot: string | undefined) => {
    const slot = blockSlot ?? inlineSlot ?? '';
    const fragment = exampleFragment(examples, slot, edition, lang);
    return options.marked ? `<!--example:${slot}-->${fragment}<!--/example:${slot}-->` : fragment;
  });
}

/** Edition slugs available in `examples` (every slot's keys, minus `default`), stable order. */
export function exampleEditions(examples: IHelpExampleSlots | undefined): string[] {
  const editions = new Set<string>();
  for (const bucket of Object.values(examples ?? {}))
    for (const edition of Object.keys(bucket)) if (edition !== DEFAULT_EXAMPLE_EDITION) editions.add(edition);
  return [...editions].sort();
}
